import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, message, Card, Tag, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import { classAPI } from '../services/api';
import './ClassManagement.css';
import {
  buildFilters,
  compareDate,
  compareNumber,
  compareText,
  createValueFilter,
} from '../utils/tableHelpers';

const { Option } = Select;
const { TextArea } = Input;

interface Class {
  id: number;
  name: string;
  grade: number;
  description?: string;
  student_count?: number;
  created_at: string;
  updated_at: string;
}

const ClassManagement: React.FC = () => {
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingClass, setEditingClass] = useState<Class | null>(null);
  const [form] = Form.useForm();

  // 获取班级列表
  const fetchClasses = async () => {
    setLoading(true);
    try {
      const response = await classAPI.list({ page_size: 100 });
      setClasses(response.data.data || []);
    } catch (error) {
      message.error('获取班级列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClasses();
  }, []);

  // 显示创建/编辑模态框
  const showModal = (classItem?: Class) => {
    if (classItem) {
      setEditingClass(classItem);
      form.setFieldsValue({
        name: classItem.name,
        grade: classItem.grade,
        description: classItem.description,
      });
    } else {
      setEditingClass(null);
      form.resetFields();
    }
    setModalVisible(true);
  };

  // 处理表单提交
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingClass) {
        // 更新班级
        await classAPI.update(editingClass.id, values);
        message.success('班级更新成功');
      } else {
        // 创建班级
        await classAPI.create(values);
        message.success('班级创建成功');
      }
      
      setModalVisible(false);
      form.resetFields();
      fetchClasses();
    } catch (error) {
      message.error(editingClass ? '更新班级失败' : '创建班级失败');
    }
  };

  // 删除班级
  const handleDelete = async (id: number) => {
    try {
      await classAPI.delete(id);
      message.success('班级删除成功');
      fetchClasses();
    } catch (error) {
      message.error('删除班级失败');
    }
  };

  // 年级选项
  const gradeOptions = [
    { value: 10, label: '高一' },
    { value: 11, label: '高二' },
    { value: 12, label: '高三' },
  ];

  // 表格列定义
  const columns = [
    {
      title: '班级名称',
      dataIndex: 'name',
      key: 'name',
      sorter: compareText((record: Class) => record.name),
      filters: buildFilters(classes, (record: Class) => record.name),
      onFilter: createValueFilter((record: Class) => record.name),
      filterSearch: true,
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: '年级',
      dataIndex: 'grade',
      key: 'grade',
      sorter: compareNumber((record: Class) => record.grade),
      filters: gradeOptions.map((option) => ({
        text: option.label,
        value: String(option.value),
      })),
      onFilter: createValueFilter((record: Class) => record.grade),
      render: (grade: number) => {
        const gradeItem = gradeOptions.find(g => g.value === grade);
        return (
          <Tag color="blue">
            {gradeItem ? gradeItem.label : `${grade}年级`}
          </Tag>
        );
      },
    },
    {
      title: '学生人数',
      dataIndex: 'student_count',
      key: 'student_count',
      sorter: compareNumber((record: Class) => record.student_count),
      render: (count: number) => (
        <Space>
          <UserOutlined />
          <span>{count || 0} 人</span>
        </Space>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      sorter: compareText((record: Class) => record.description),
      filters: buildFilters(classes, (record: Class) => record.description, {
        includeEmpty: true,
        emptyText: '未填写描述',
      }),
      onFilter: createValueFilter((record: Class) => record.description),
      filterSearch: true,
      render: (text: string) => text || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: compareDate((record: Class) => record.created_at),
      render: (text: string) => new Date(text).toLocaleDateString(),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Class) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => showModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个班级吗？"
            description="删除后，该班级下的学生信息将被保留但不再关联到此班级。"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="class-management">
      <Card
        title="班级管理"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showModal()}
          >
            创建班级
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={classes}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 个班级`,
          }}
        />
      </Card>

      <Modal
        title={editingClass ? '编辑班级' : '创建班级'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ grade: 12 }}
        >
          <Form.Item
            name="name"
            label="班级名称"
            rules={[{ required: true, message: '请输入班级名称' }]}
          >
            <Input placeholder="请输入班级名称，如：高一(1)班" />
          </Form.Item>

          <Form.Item
            name="grade"
            label="年级"
            rules={[{ required: true, message: '请选择年级' }]}
          >
            <Select placeholder="请选择年级">
              {gradeOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="班级描述"
          >
            <TextArea
              rows={3}
              placeholder="请输入班级描述（选填）"
              maxLength={200}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ClassManagement;
